import React, { useState } from 'react';
import { returnService } from '../services/api';

function ReturnList({ returns, isDeposito, isSupermercado, onStatusUpdate }) {
  const [expandedReturns, setExpandedReturns] = useState({});
  const [historyData, setHistoryData] = useState({});
  const [loadingHistory, setLoadingHistory] = useState({});
  const [confirmModal, setConfirmModal] = useState(null);
  const [confirmedQuantities, setConfirmedQuantities] = useState({});
  const [showConfirmedReturn, setShowConfirmedReturn] = useState({});

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('es-UY', { timeZone: 'America/Montevideo' });
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'CREADO':
        return 'Creado';
      case 'ENVIADO':
        return 'Enviado';
      case 'RECIBIDO':
        return 'Recibido';
      default:
        return status;
    }
  };

  const toggleExpand = (returnId) => {
    setExpandedReturns(prev => ({
      ...prev,
      [returnId]: !prev[returnId]
    }));
  };

  const loadHistory = async (returnId) => {
    if (historyData[returnId]) {
      return;
    }
    
    setLoadingHistory(prev => ({ ...prev, [returnId]: true }));
    try {
      const response = await returnService.getReturnHistory(returnId);
      if (response.success) {
        setHistoryData(prev => ({ ...prev, [returnId]: response.data || [] }));
      }
    } catch (err) {
      console.error('Error loading history:', err);
    } finally {
      setLoadingHistory(prev => ({ ...prev, [returnId]: false }));
    }
  };

  const toggleHistory = (returnId) => {
    if (!historyData[returnId]) {
      loadHistory(returnId);
    }
  };

  const getTotalItems = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => sum + item.cantidad, 0);
  };

  const getTotalConfirmedItems = (items) => {
    if (!items || items.length === 0) return 0;
    return items.reduce((sum, item) => sum + (item.cantidadConfirmada || 0), 0);
  };

  const getDiffClassName = (diff) => {
    if (diff < 0) return 'diff-negative';
    if (diff > 0) return 'diff-positive';
    return '';
  };

  const formatDiff = (diff) => {
    return diff > 0 ? `+${diff}` : diff.toString();
  };

  const openConfirmModal = (returnDoc) => {
    const initialQuantities = {};
    returnDoc.items.forEach(item => {
      initialQuantities[item.id] = item.cantidad;
    });
    setConfirmedQuantities(initialQuantities);
    setConfirmModal(returnDoc);
  };

  const closeConfirmModal = () => {
    setConfirmModal(null);
    setConfirmedQuantities({});
  };

  const handleQuantityChange = (itemId, value) => {
    // Allow empty string for editing
    if (value === '') {
      setConfirmedQuantities(prev => ({
        ...prev,
        [itemId]: ''
      }));
    } else {
      const numValue = parseInt(value, 10);
      setConfirmedQuantities(prev => ({
        ...prev,
        [itemId]: isNaN(numValue) ? 0 : Math.max(0, numValue)
      }));
    }
  };

  const handleConfirmSubmit = async () => {
    try {
      const confirmedItems = Object.entries(confirmedQuantities).map(([itemId, cantidad]) => ({
        itemId: parseInt(itemId),
        cantidadConfirmada: cantidad
      }));

      await onStatusUpdate(confirmModal.id, 'RECIBIDO', confirmedItems);
      closeConfirmModal();
    } catch (error) {
      console.error('Error confirming return:', error);
    }
  };

  const handleStatusChange = async (returnId, newStatus) => {
    try {
      await onStatusUpdate(returnId, newStatus);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleDownloadPdf = async (returnId) => {
    try {
      await returnService.downloadReturnPdf(returnId);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error al descargar el PDF');
    }
  };

  const toggleShowConfirmed = (returnId) => {
    setShowConfirmedReturn(prev => ({
      ...prev,
      [returnId]: !prev[returnId]
    }));
  };

  const canChangeStatus = (returnDoc) => {
    if (isSupermercado && returnDoc.status === 'CREADO') {
      return true;
    }
    if (isDeposito && returnDoc.status === 'ENVIADO') {
      return true;
    }
    return false;
  };

  const getNextStatus = (currentStatus) => {
    if (currentStatus === 'CREADO') return 'ENVIADO';
    if (currentStatus === 'ENVIADO') return 'RECIBIDO';
    return null;
  };

  const getStatusButtonLabel = (currentStatus) => {
    const nextStatus = getNextStatus(currentStatus);
    return nextStatus ? `Marcar como ${getStatusLabel(nextStatus)}` : '';
  };

  if (!returns || returns.length === 0) {
    return <p>No hay devoluciones para mostrar.</p>;
  }

  return (
    <div className="order-list">
      {returns.map((returnDoc) => {
        const totalSolicitado = getTotalItems(returnDoc.items);
        const totalConfirmado = getTotalConfirmedItems(returnDoc.items);
        const hasConfirmed = totalConfirmado > 0;
        const diff = hasConfirmed ? totalConfirmado - totalSolicitado : 0;

        return (
          <div key={returnDoc.id} className="order-card">
            <div className="order-header" onClick={() => toggleExpand(returnDoc.id)}>
              <div className="order-info">
                <div className="order-title">
                  <h3>Devolución #{returnDoc.id}</h3>
                  <span className={`status-badge status-${returnDoc.status.toLowerCase()}`}>
                    {getStatusLabel(returnDoc.status)}
                  </span>
                </div>
                <p className="order-description">{returnDoc.descripcion}</p>
                <div className="order-meta">
                  <span className="supermercado-highlight">
                    <strong>Supermercado:</strong> {returnDoc.supermercadoNombre || 'N/A'}
                  </span>
                  <span>Creado por: {returnDoc.createdByUsername}</span>
                  <span>Fecha: {formatDate(returnDoc.createdAt)}</span>
                  <span>Productos: {returnDoc.items.length}</span>
                  <span>Cantidad total: {totalSolicitado}</span>
                  {hasConfirmed && (
                    <>
                      <span>Confirmado: {totalConfirmado}</span>
                      {diff !== 0 && (
                        <span className={getDiffClassName(diff)}>
                          Diferencia: {formatDiff(diff)}
                        </span>
                      )}
                    </>
                  )}
                </div>
              </div>
              <span className="expand-icon">{expandedReturns[returnDoc.id] ? '▼' : '▶'}</span>
            </div>

            {expandedReturns[returnDoc.id] && (
              <div className="order-details">
                <h4>Productos</h4>
                <table className="items-table">
                  <thead>
                    <tr>
                      <th>Producto</th>
                      <th>Código</th>
                      <th>Cantidad</th>
                      {hasConfirmed && showConfirmedReturn[returnDoc.id] && (
                        <>
                          <th>Confirmada</th>
                          <th>Diferencia</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {returnDoc.items.map((item) => {
                      const itemDiff = item.cantidadConfirmada 
                        ? item.cantidadConfirmada - item.cantidad 
                        : 0;
                      
                      return (
                        <tr key={item.id}>
                          <td>{item.productNombre}</td>
                          <td>{item.productCodigo}</td>
                          <td>{item.cantidad}</td>
                          {hasConfirmed && showConfirmedReturn[returnDoc.id] && (
                            <>
                              <td>{item.cantidadConfirmada || '-'}</td>
                              <td className={getDiffClassName(itemDiff)}>
                                {itemDiff !== 0 ? formatDiff(itemDiff) : '-'}
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {hasConfirmed && (
                  <button 
                    onClick={() => toggleShowConfirmed(returnDoc.id)}
                    className="btn-secondary small-btn"
                    style={{ marginTop: '10px' }}
                  >
                    {showConfirmedReturn[returnDoc.id] 
                      ? 'Ocultar cantidades confirmadas' 
                      : 'Ver cantidades confirmadas'}
                  </button>
                )}

                <div className="order-actions">
                  <button 
                    onClick={() => handleDownloadPdf(returnDoc.id)}
                    className="btn-secondary"
                  >
                    Descargar PDF
                  </button>

                  <button 
                    onClick={() => toggleHistory(returnDoc.id)}
                    className="btn-secondary"
                  >
                    {historyData[returnDoc.id] ? 'Ocultar' : 'Ver'} Historial
                  </button>

                  {canChangeStatus(returnDoc) && (
                    <button
                      onClick={() => {
                        if (returnDoc.status === 'ENVIADO' && isDeposito) {
                          openConfirmModal(returnDoc);
                        } else {
                          handleStatusChange(returnDoc.id, getNextStatus(returnDoc.status));
                        }
                      }}
                      className="btn-primary"
                    >
                      {getStatusButtonLabel(returnDoc.status)}
                    </button>
                  )}
                </div>

                {historyData[returnDoc.id] && (
                  <div className="order-history">
                    <h4>Historial de Estados</h4>
                    {loadingHistory[returnDoc.id] ? (
                      <p>Cargando historial...</p>
                    ) : historyData[returnDoc.id].length === 0 ? (
                      <p>No hay historial de cambios.</p>
                    ) : (
                      <table className="history-table">
                        <thead>
                          <tr>
                            <th>Estado Anterior</th>
                            <th>Estado Nuevo</th>
                            <th>Cambiado por</th>
                            <th>Fecha</th>
                            <th>Notas</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historyData[returnDoc.id].map((history) => (
                            <tr key={history.id}>
                              <td>{getStatusLabel(history.previousStatus)}</td>
                              <td>{getStatusLabel(history.newStatus)}</td>
                              <td>{history.changedByUsername}</td>
                              <td>{formatDate(history.changedAt)}</td>
                              <td>{history.notes || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {confirmModal && (
        <div className="modal-overlay" onClick={closeConfirmModal}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Confirmar Devolución #{confirmModal.id}</h2>
              <button onClick={closeConfirmModal} className="btn-close">&times;</button>
            </div>
            <div className="modal-body">
              <p className="modal-description">
                Ingrese las cantidades realmente recibidas. Puede modificar las cantidades si es necesario.
              </p>
              
              <table className="confirm-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Producto</th>
                    <th>Cantidad Solicitada</th>
                    <th>Cantidad Recibida</th>
                  </tr>
                </thead>
                <tbody>
                  {confirmModal.items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.productCodigo}</td>
                      <td>{item.productNombre}</td>
                      <td>{item.cantidad}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          value={confirmedQuantities[item.id] ?? ''}
                          onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                          onBlur={(e) => {
                            // Ensure valid quantity on blur
                            if (e.target.value === '') {
                              handleQuantityChange(item.id, '0');
                            }
                          }}
                          className="quantity-input"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="modal-footer">
              <button onClick={closeConfirmModal} className="btn btn-secondary">
                Cancelar
              </button>
              <button onClick={handleConfirmSubmit} className="btn btn-success">
                Confirmar Recepción
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ReturnList;
